import {
    DragElement,
    DragElementArea,
    FIMoreH,
    Icon,
    Text,
    View,
    classNames,
    useCustomEvent,
    useDrag,
    useDragEvent,
    useEvent,
    useStorage,
    waitForRender,
    windowObject,
} from '@fold-dev/core'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getCustomGhostElement, scrollDown } from '../../helpers'
import { FOLD_TODO_SECTION_MOUSEOVER_CACHE, TodoAddTask, TodoSectionHeader, TodoTasks } from './'
import { TodoAddSection } from './todo-add-section'
import { TodoContext } from './todo.provider'
import { TodoTypes } from './todo.types'
import { getCollapsedStateFromLocalStorage } from './todo.util'
import { TbDots } from 'react-icons/tb'

export const TASK_SECTION_AREAID_CACHE = 'TASK_SECTION_AREAID_CACHE'
export const TASK_SECTION_COLLAPSED = 'TASK_SECTION_COLLAPSED'

export type TodoSectionProps = {
    index?: number
    hide?: boolean
} & TodoTypes.Section

export const TodoSection = (props: TodoSectionProps) => {
    const { index, hide, id, title, description, locked, prefix, color, position, tasks = [] } = props
    const areaId = `i-${index}`
    const sectionIndex = index
    const containerRef = useRef(null)
    const mouseOverTimer = useRef(null)
    const { getStaticState } = useDrag()
    const { getStorage, setStorage } = useStorage()
    const {
        nested,
        onTaskAdd,
        onMultipleTaskAdd,
        onSectionAdd,
        noSectionAdd,
        noTaskLabels,
        noSelection,
        noDragging,
        kanbanMode,
        noBlankSection,
    } = useContext(TodoContext)
    const { setCustomGhostElement, setCustomGhostElementRotation } = useDrag()
    const [collapsed, setCollapsed] = useState(getCollapsedStateFromLocalStorage(TASK_SECTION_COLLAPSED, id))
    const [targetSection, setTargetSection] = useState(0)
    const [originGroup, setOriginGroup] = useState('')
    const completed = useMemo(() => tasks?.filter((task) => task.complete).length ?? 0, [tasks])
    const header = useMemo(() => {
        if (kanbanMode && !!tasks && !!tasks.length) return true
        return index == 0 && noBlankSection ? false : true
    }, [kanbanMode, tasks?.length, index, noBlankSection])
    const className = classNames({
        'f-todo-section': true,
        'is-collapsed': collapsed,
        'is-hidden': hide,
    })

    const handleMultipleTaskAdd = (tasks) => {
        onMultipleTaskAdd &&
            onMultipleTaskAdd({
                tasks,
                sectionIndex: index,
            })
    }

    const handleTaskAdd = (task) => {
        onTaskAdd({
            task,
            sectionIndex: index,
        })
    }

    const handleMouseDown = (e) => {
        setCustomGhostElementRotation('0deg')
        setCustomGhostElement(getCustomGhostElement(title, 0))
    }

    const handleMouseLeave = (e) => {
        setTargetSection(null)
        clearTimeout(mouseOverTimer.current)
    }

    const handleMouseEnter = (e) => {
        const { target, origin } = getStaticState()

        // we use the last index/id here when the card is dropped
        windowObject[FOLD_TODO_SECTION_MOUSEOVER_CACHE] = {
            areaId,
            elementId: tasks.length ? tasks[tasks.length - 1].id : null,
            index: tasks.length ? tasks.length - 1 : 0,
        }

        if (!collapsed) {
            // if its a list with tasks
            let flag = !kanbanMode && !!tasks.length

            // if there is only 1 card and it moves off then back in
            // then show the ghost placeholder
            if (windowObject[FOLD_TODO_SECTION_MOUSEOVER_CACHE]?.areaId == origin.areaId && tasks.length == 1)
                flag = false

            if (flag) return

            mouseOverTimer.current = setInterval(() => {
                const { target, origin } = getStaticState()

                if (origin.group == 'tasks' && !!origin.areaId) {
                    // if it's a different area and it's a task list
                    // this had: tasks.length == 1 ??? why?
                    if (windowObject[FOLD_TODO_SECTION_MOUSEOVER_CACHE]?.areaId != origin.areaId && !kanbanMode) {
                        // this never fires???
                        setTargetSection(origin.height)
                    } else if (windowObject[FOLD_TODO_SECTION_MOUSEOVER_CACHE]?.areaId != target.areaId) {
                        setTargetSection(origin.height)
                    } else {
                        setTargetSection(null)
                    }
                } else {
                    setTargetSection(null)
                }
            }, 250)
        }
    }

    const handleMouseUp = (e) => {
        waitForRender(() => {
            setTargetSection(null)
            clearTimeout(mouseOverTimer.current)
        })
    }

    const handleSectionAdd = (title: string) => {
        onSectionAdd({
            title,
            sectionIndex: index,
        })
    }

    const handleCollapsed = (collapsed) => {
        const sectionsCollapsed = JSON.parse(getStorage(TASK_SECTION_COLLAPSED) || '{}')

        if (collapsed) {
            sectionsCollapsed[id] = true
        } else {
            delete sectionsCollapsed[id]
        }

        setStorage(TASK_SECTION_COLLAPSED, JSON.stringify(sectionsCollapsed))
        setCollapsed(collapsed)
    }

    const handleDragEvent = ({ detail }) => {
        if (!detail) return
        const { origin, target } = detail
        if (origin) setOriginGroup(origin.group)
    }

    const handleScrollDown = ({ detail }) => {
        if (!detail) return
        if (kanbanMode && detail.sectionId == id) scrollDown(containerRef.current)
    }

    useEvent('mouseup', handleMouseUp)
    useDragEvent('onstart', handleDragEvent)
    useCustomEvent('todosection:scroll-down', handleScrollDown)

    useEffect(() => {
        if (id) setCollapsed(getCollapsedStateFromLocalStorage(TASK_SECTION_COLLAPSED, id))
    }, [id])

    return (
        <DragElement
            noDrag={locked ? true : undefined}
            noDrop={locked ? true : undefined}
            zIndex={100 - index}
            className={className}>
            <div
                className={
                    originGroup == 'tasks' ? 'f-todo-section__tasks-outer drag-pe-all' : 'f-todo-section__tasks-outer'
                }
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}>
                {header && (
                    <TodoSectionHeader
                        id={id}
                        title={title}
                        description={description}
                        prefix={prefix}
                        color={color}
                        tasks={tasks.length}
                        tasksCompleted={completed}
                        collapsed={collapsed}
                        onCollapsed={handleCollapsed}
                    />
                )}

                {!header && <br />}

                {!collapsed && (
                    <>
                        {/*       
                        <View
                            className="f-todo-task-item__titles"
                            row
                            bgToken="surface"
                            p="0.5rem 0rem 1rem 2rem"
                            width="100%">
                            <Text
                                size="sm"
                                fontWeight={600}
                                flex={1}
                                colorToken="text-weaker">
                                Title
                            </Text>
                            {!noTaskLabels && (
                                <div
                                    className="f-todo-task-item__labels f-row"
                                    style={{ border: 0, height: 'fit-content' }}>
                                    <Icon icon={TbDots} />
                                    <Text
                                        size="sm"
                                        fontWeight={600}
                                        colorToken="text-weaker">
                                        Labels
                                    </Text>
                                </div>
                            )}
                            <div
                                className="f-todo-task-item__date f-row"
                                style={{ border: 0, height: 'fit-content' }}>
                                <Icon icon={TbDots} />
                                <Text
                                    size="sm"
                                    fontWeight={600}
                                    colorToken="text-weaker">
                                    Due
                                </Text>
                            </div>
                            <div
                                className="f-todo-task-item__users f-row"
                                style={{ border: 0, height: 'fit-content' }}>
                                <Icon icon={TbDots} />
                                <Text
                                    size="sm"
                                    fontWeight={600}
                                    colorToken="text-weaker">
                                    Users
                                </Text>
                            </div>
                        </View> 
                        */}

                        <div
                            ref={containerRef}
                            className="f-todo-section__tasks f-scrollbar">
                            <DragElementArea
                                indent
                                group="tasks"
                                variant={nested ? 'lined' : 'animated'}
                                areaId={areaId}
                                targetVariant={{ projects: 'focus' }}
                                className="f-todo-section__tasks-inner"
                                disabled={noDragging}
                                footer={
                                    !!targetSection ? (
                                        <div
                                            className="f-todo-section__placeholder"
                                            style={{ height: targetSection }}
                                        />
                                    ) : null
                                }>
                                <TodoTasks
                                    tasks={tasks}
                                    indent={0}
                                    visible={true}
                                    freeze={false}
                                />
                            </DragElementArea>
                        </div>
                    </>
                )}

                {!collapsed && (
                    <>
                        <TodoAddTask
                            onAdd={handleTaskAdd}
                            onAddMultiple={handleMultipleTaskAdd}
                        />
                        {!noSectionAdd && <TodoAddSection onAdd={handleSectionAdd} />}
                    </>
                )}
            </div>
        </DragElement>
    )
}
